import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Link2, Loader2, Paperclip } from "lucide-react";
import { TaskQuickActions } from "../engine/TaskQuickActions";
import { STATE_BADGE, badge } from "../engine/taskMeta";

/* 分包商工作台「我的工作安排」：只看本工种关联任务；上传证据 / 留言 同时进入完工验收资料 */
export const PortalTaskChain = ({ sc, api, zh, reload }) => {
  const sid = sc.id;
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);
  const [logs, setLogs] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/my/subcontracts/${sid}/tasks`); setD(data); setErr(""); }
    catch (e) { setErr(e?.detailText || (zh ? "加载失败" : "Load failed")); }
  }, [api, sid, zh]);
  useEffect(() => { load(); }, [load]);
  const loadLogs = useCallback((tid) => {
    if (!tid) { setLogs([]); return; }
    api.get(`/subcontracts/${sid}/tasks/${tid}/logs`).then(({ data }) => setLogs(data || [])).catch(() => setLogs([]));
  }, [api, sid]);
  useEffect(() => { loadLogs(sel); }, [sel, loadLogs]);
  const changed = () => { load(); loadLogs(sel); reload?.(); };
  if (err) return <p className="text-xs text-red-600" data-testid={`portal-tasks-error-${sid}`}>{err}</p>;
  if (!d) return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />;
  const msName = (mid) => d.milestones.find((m) => m.id === mid)?.name || mid;
  const openTasks = d.groups.flatMap((g) => g.tasks).filter((t) => !["done", "skipped"].includes(t.state));
  const cur = openTasks.find((t) => t.state === "ready") || openTasks[0];
  return (
    <div className="space-y-2" data-testid={`portal-tasks-${sid}`} data-done={d.doneCount} data-total={d.linkedCount}>
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className="text-slate-600">{zh ? "本工种关联任务" : "Linked tasks"} <b className="text-slate-800">{d.doneCount}/{d.linkedCount}</b> {zh ? "已完成" : "done"}</span>
        <span className="h-1.5 w-24 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-emerald-500" style={{ width: `${d.linkedCount ? Math.round((d.doneCount / d.linkedCount) * 100) : 0}%` }} /></span>
        {cur && <span className="text-[11px] text-brand-700 bg-brand-50 px-2 py-0.5 rounded-full truncate max-w-full" data-testid={`portal-tasks-current-${sid}`}>{zh ? "当前：" : "Now: "}{zh ? cur.title : (cur.titleEn || cur.title)}</span>}
      </div>
      <p className="text-[10px] text-slate-400">{zh ? "上传的证据 / 照片会同时进入「竣工验收资料」并关联对应里程碑；留言会通知项目经理。任务由项目小组确认完成。" : "Evidence you upload also goes to completion documents; comments notify the PM. Tasks are completed by the project team."}</p>
      {d.groups.map((g) => (
        <div key={g.sub} data-testid={`portal-tasks-group-${sid}-${g.sub}`}>
          <p className="text-[10px] font-semibold text-slate-500 mb-0.5">{zh ? g.subName : (g.subEn || g.subName)}</p>
          {g.tasks.map((t) => {
            const b = badge(STATE_BADGE, t.state, zh);
            const on = sel === t.id;
            return (
              <div key={t.id} data-testid={`portal-task-${sid}-${t.node}`} data-state={t.state} className={`rounded-md border mb-1 ${on ? "border-brand-300 bg-brand-50/30" : "border-slate-100 bg-slate-50/50"}`}>
                <button type="button" onClick={() => setSel(on ? null : t.id)} data-testid={`portal-task-toggle-${sid}-${t.node}`} className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left text-xs">
                  {on ? <ChevronDown className="h-3 w-3 text-slate-400 shrink-0" /> : <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />}
                  <span className={`flex-1 ${["done", "skipped"].includes(t.state) ? "line-through text-slate-400" : "text-slate-800"}`}>{zh ? t.title : (t.titleEn || t.title)}</span>
                  {t.milestoneIds.length > 0 && <span title={t.milestoneIds.map(msName).join("、")} className="inline-flex items-center gap-0.5 text-[9px] text-purple-700 shrink-0" data-testid={`portal-task-ms-${sid}-${t.node}`}><Link2 className="h-2.5 w-2.5" />{zh ? "付款条件" : "milestone"}</span>}
                  {t.evidence.length > 0 && <span className="inline-flex items-center text-[9px] text-slate-400 shrink-0"><Paperclip className="h-2.5 w-2.5" />{t.evidence.length}</span>}
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${b.cls}`}>{b.label}</span>
                </button>
                {on && (
                  <div className="border-t border-slate-100 px-2 py-1.5" data-testid={`portal-task-detail-${sid}-${t.node}`}>
                    {t.milestoneIds.length > 0 && <p className="text-[10px] text-purple-700 mb-1">{zh ? "完成后计入里程碑：" : "Counts toward: "}{t.milestoneIds.map(msName).join("、")}</p>}
                    <TaskQuickActions api={api} pid={sc.projectId} sid={sid} task={t} zh={zh} mode="portal" logs={logs} onChanged={changed} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {!d.linkedCount && <p className="text-xs text-slate-400" data-testid={`portal-tasks-empty-${sid}`}>{zh ? "暂无关联任务（由项目小组设置工种任务范围）" : "No linked tasks yet"}</p>}
    </div>
  );
};
